import React from 'react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';
import { styled } from '@mui/material';
import useAnalytics, { eventName } from '@/hooks/use-analytics';

interface SelectOption {
  value: string;
  label: string;
  sublabel?: string;
}

type SelectStringProps = Omit<SelectProps<string>, 'children' | 'label' | 'variant'> & { value: string };

interface BaseSelectDropdownProps {
  label: string;
  options: SelectOption[];
  fullWidth?: boolean;
  disabled?: boolean;
  id?: string;
  error?: boolean;
  helperText?: string;
  selectProps: SelectStringProps;
  fireAnalytics?: boolean;
}

export const BaseSelectDropdown: React.FC<BaseSelectDropdownProps> = ({
  label,
  options,
  fullWidth = true,
  disabled,
  id,
  error,
  helperText,
  selectProps,
  fireAnalytics = true,
}) => {
  const labelId = id ? `${id}-label` : undefined;
  const helperId = id ? `${id}-helper-text` : undefined;
  const { trackEvent } = useAnalytics();

  return (
    <StyledFormControl
      fullWidth={fullWidth}
      disabled={disabled}
      error={!!error}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <StyledSelect
        {...selectProps}
        variant='outlined'
        id={id}
        labelId={labelId}
        label={label}
        aria-describedby={helperText ? helperId : undefined}
        onChange={(ev, el) => {
          if (fireAnalytics) trackEvent(`${eventName(label)}_CHANGED`);
          if (selectProps.onChange) selectProps.onChange(ev, el);
        }}
        renderValue={(selected) => {
          const selectedOption = options.find((o) => o.value === selected);
          return selectedOption ? (
            <StyledLabel
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                paddingRight: '16px',
              }}
            >
              {selectedOption.label}
            </StyledLabel>
          ) : null;
        }}
      >
        {options.map((o) => (
          <MenuItem
            key={o.value}
            value={o.value}
          >
            <StyledLabel>{o.label}</StyledLabel>
            {o.sublabel && <StyledSublabel>{o.sublabel}</StyledSublabel>}
          </MenuItem>
        ))}
      </StyledSelect>
      {helperText ? <FormHelperText id={helperId}>{helperText}</FormHelperText> : null}
    </StyledFormControl>
  );
};

const StyledFormControl = styled(FormControl)`
  & .MuiFormLabel-root.Mui-focused {
    color: var(--mui-palette-tertiary-main);
  }
`;

const StyledSelect = styled((props: SelectProps<string>) => <Select<string> {...props} />)`
  &.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: var(--mui-palette-tertiary-main);
  }
`;

const StyledLabel = styled('span')`
  flex: 1;
  white-space: wrap;
  max-width: 80%;
`;

const StyledSublabel = styled('span')`
  margin-left: auto;
  color: var(--mui-palette-text-secondary);
  font-weight: 400;
`;
